import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { ISSUE_STATUSES, ISSUE_STATUS_LABEL, PRIORITIES, PRIORITY_LABEL, PRIORITY_CLASS } from "../projectMeta";
import StickyHead from "../StickyHead";

const EMPTY = { title: "", project_id: "", priority: "NORMAL", body: "" };

export default function IssuesPage() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [projects, setProjects] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function load(status = "") {
    setError("");
    try {
      setRows(await api.get(`/issues${status ? `?status=${status}` : ""}`));
    } catch (err) { setError(err.message); }
  }
  useEffect(() => {
    load();
    (async () => {
      try { setProjects(await api.get("/projects")); } catch { /* 무시 */ }
    })();
  }, []);

  function onFilter(s) { setFilter(s); load(s); }

  async function add(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError("제목은 필수입니다."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/issues", {
        title: form.title,
        project_id: form.project_id ? Number(form.project_id) : null,
        priority: form.priority,
        body: form.body || null,
      });
      setForm(EMPTY); setAdding(false); load(filter);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function toggle(it) {
    const next = it.status === "OPEN" ? "RESOLVED" : "OPEN";
    try { await api.post(`/issues/${it.id}`, { status: next }); load(filter); } catch (err) { setError(err.message); }
  }
  async function remove(id) {
    if (!window.confirm("이슈를 삭제할까요?")) return;
    try { await api.del(`/issues/${id}`); load(filter); } catch (err) { setError(err.message); }
  }

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>
        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">이슈</h1>
            <p className="sstoa-sub">처리해야 할 문제·안건을 관리합니다.</p>
          </div>
          {!adding && <button className="sstoa-btn sstoa-btn--primary" onClick={() => setAdding(true)}>+ 이슈 등록</button>}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button className={`sstoa-btn sstoa-btn--sm${filter === "" ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter("")}>전체</button>
          {ISSUE_STATUSES.map(([v, l]) => (
            <button key={v} className={`sstoa-btn sstoa-btn--sm${filter === v ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter(v)}>{l}</button>
          ))}
        </div>
      </StickyHead>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      {adding && (
        <form className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 12 }} onSubmit={add}>
          <div className="sstoa-row">
            <div className="sstoa-field" style={{ flex: 2 }}>
              <label>제목 <span className="req">*</span></label>
              <input className="sstoa-input" value={form.title} onChange={upd("title")} disabled={busy} />
            </div>
            <div className="sstoa-field">
              <label>우선순위</label>
              <select className="sstoa-select" value={form.priority} onChange={upd("priority")} disabled={busy}>
                {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="sstoa-field" style={{ marginTop: 12 }}>
            <label>관련 프로젝트 (선택)</label>
            <select className="sstoa-select" value={form.project_id} onChange={upd("project_id")} disabled={busy}>
              <option value="">연결 안 함</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="sstoa-field" style={{ marginTop: 12 }}>
            <label>내용</label>
            <textarea className="sstoa-textarea" value={form.body} onChange={upd("body")} disabled={busy} />
          </div>
          <div className="sstoa-actions" style={{ marginTop: 12 }}>
            <button type="submit" className="sstoa-btn sstoa-btn--primary sstoa-btn--sm" disabled={busy}>등록</button>
            <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => { setAdding(false); setForm(EMPTY); }}>취소</button>
          </div>
        </form>
      )}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">이슈가 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead><tr><th>상태</th><th>제목</th><th>프로젝트</th><th>우선순위</th><th></th></tr></thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.id} style={{ cursor: "default" }}>
                  <td>
                    <button className={`sstoa-tag ${it.status === "RESOLVED" ? "sstoa-tag--gray" : ""}`} style={{ border: 0, cursor: "pointer" }} onClick={() => toggle(it)} title="클릭하여 상태 전환">
                      {ISSUE_STATUS_LABEL[it.status] || it.status}
                    </button>
                  </td>
                  <td style={{ fontWeight: 600, textDecoration: it.status === "RESOLVED" ? "line-through" : "none", color: it.status === "RESOLVED" ? "var(--s-fg-mute)" : "inherit" }}>
                    {it.title}
                    {it.body && <div style={{ fontWeight: 400, fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2, whiteSpace: "pre-wrap" }}>{it.body}</div>}
                  </td>
                  <td>
                    {it.project
                      ? <button className="sstoa-link" style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }} onClick={() => navigate(`/sstoa/projects/${it.project.id}`)}>{it.project.name}</button>
                      : "—"}
                  </td>
                  <td><span className={`sstoa-tag ${PRIORITY_CLASS[it.priority] || ""}`}>{PRIORITY_LABEL[it.priority] || it.priority}</span></td>
                  <td style={{ textAlign: "right" }}>
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(it.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
